import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the API tab; each part uses its own slice of it */
import styles from './apiAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */

const MAX_NAME_LENGTH = 100;

interface ApiAccessCreateFormProps {
  onCreate: (input: { name: string }) => void;
  /** Omitted when the form is the only thing on screen — there is nothing to go back to. */
  onCancel?: () => void;
  isCreating: boolean;
  createError: Error | null;
}

/**
 * Minting a token asks for one thing: a name you will recognise in the table
 * six months from now. Scope and expiry are not modelled server-side, so this
 * form deliberately offers neither.
 */
const ApiAccessCreateFormComponent: React.FC<ApiAccessCreateFormProps> = ({
  onCreate,
  onCancel,
  isCreating,
  createError,
}) => {
  const intl = useIntl();
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const canCreate = trimmed.length > 0 && !isCreating;

  const onNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value), []);

  const submit = useCallback(() => {
    if (trimmed.length === 0) {
      return;
    }
    onCreate({ name: trimmed });
    setName('');
  }, [onCreate, trimmed]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && canCreate) {
        e.preventDefault();
        submit();
      }
      if (e.key === 'Escape' && onCancel !== undefined) {
        onCancel();
      }
    },
    [canCreate, onCancel, submit]
  );

  const nameLabel = intl.formatMessage({
    id: 'app.sml.settings.apiAccess.create.nameLabel',
    defaultMessage: 'Token name',
  });

  return (
    <Panel nested className={styles.create}>
      <label className={styles.fieldLabel} htmlFor="api-token-name">
        {nameLabel}
      </label>
      <input
        id="api-token-name"
        type="text"
        className={styles.input}
        value={name}
        onChange={onNameChange}
        onKeyDown={onKeyDown}
        maxLength={MAX_NAME_LENGTH}
        aria-label={nameLabel}
        placeholder={intl.formatMessage({
          id: 'app.sml.settings.apiAccess.create.namePlaceholder',
          defaultMessage: 'e.g. CI pipeline',
        })}
      />

      {createError !== null && (
        <Alert color="danger" className="mt-3 mb-0">
          {createError.message}
        </Alert>
      )}

      <div className={styles.createFoot}>
        <span className={styles.hint}>
          {intl.formatMessage({
            id: 'app.sml.settings.apiAccess.create.hint',
            defaultMessage: 'Shown once, then only its prefix.',
          })}
        </span>
        <span className={styles.spacer} />
        {onCancel !== undefined && (
          <Button color="light" onClick={onCancel} disabled={isCreating}>
            {intl.formatMessage({ id: 'app.sml.settings.apiAccess.create.cancel', defaultMessage: 'Cancel' })}
          </Button>
        )}
        <Button color="primary" onClick={submit} disabled={!canCreate}>
          {intl.formatMessage({ id: 'app.sml.settings.apiAccess.create.submit', defaultMessage: 'Create token' })}
        </Button>
      </div>
    </Panel>
  );
};

export const ApiAccessCreateForm = TypedMemo(ApiAccessCreateFormComponent);
